import { ReactNode } from 'react';
import { Form as AntForm, Select, Tooltip } from 'antd';
import { AnySchema } from 'yup';
import { Controller, Control } from 'react-hook-form';
import styled from 'styled-components';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { HSMSIcon } from '@components/shared/HSMSIcon';
import { formHelpers } from '@helpers/formHelpers';

const { Item: FormItem } = AntForm;
const { Option } = Select;

const SelectContainer = styled.div`
  display: flex;
  width: 100%;
`;

const InfoIconContainer = styled.div`
  margin-left: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface HSMSSelectOption {
  label: string | number;
  value: string | number;
}

export interface HSMSSelectProps {
  label?: string;
  name: string;
  size?: SizeType;
  options: HSMSSelectOption[] | undefined;
  validationSchema?: AnySchema;
  dropdownMatchSelectWidth?: boolean | number;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: any; // If we are setting proper types we need to get the form values. Not possible since we use this in multiple forms
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect?: (...args: any) => void;
  loading?: boolean;
  helperText?: ReactNode | undefined;
  errorField?: string;
  defaultValue?: string;
}

export const HSMSSelect = ({
  label,
  name,
  size,
  options,
  disabled,
  validationSchema,
  onSelect,
  control,
  errors,
  dropdownMatchSelectWidth,
  loading,
  helperText,
  errorField,
  defaultValue,
}: HSMSSelectProps) => (
  <FormItem
    label={label}
    required={validationSchema && formHelpers.isRequired(validationSchema, name)}
    validateStatus={errors && errors[errorField || name] ? 'error' : ''}
    help={errors && errors[errorField || name]?.message}
    labelCol={{ span: 24 }}
  >
    <SelectContainer className="select-container">
      <Controller
        defaultValue={defaultValue}
        as={
          <Select
            dropdownMatchSelectWidth={dropdownMatchSelectWidth}
            disabled={disabled}
            size={size}
            loading={loading}
            onSelect={onSelect}
            showSearch
            filterOption={(input, option) => {
              if (option && option.props.children) {
                return option.props.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }

              return false;
            }}
          >
            {options?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        }
        name={name}
        control={control}
      />
      {helperText && (
        <Tooltip title={helperText}>
          <InfoIconContainer>
            <HSMSIcon name="help" />
          </InfoIconContainer>
        </Tooltip>
      )}
    </SelectContainer>
  </FormItem>
);
